/**
 * Consumíveis de Partículas Magnéticas — cadastro global de END.
 *
 * Catálogo próprio, separado do de Líquido Penetrante: um consumível PM nunca
 * aparece na aba LP e vice-versa. Global (sem escopo de Cliente), como os
 * procedimentos e os catálogos técnicos. O futuro Relatório PM seleciona
 * daqui e copia o texto no momento da seleção — uma edição posterior do
 * cadastro nunca reescreve um documento já emitido.
 */

import { formatCivilDateBR, isLpConsumableExpired, todayIso } from "./lp-consumable.js";

/**
 * Tipos de consumível PM. Lista configurável: expandir é acrescentar uma
 * entrada aqui — o registro guarda só o código.
 */
export const PM_CONSUMABLE_TYPES = [
  { code: "MAGNETIC_PARTICLES", label: "Partículas Magnéticas" },
  { code: "CONTRAST_PAINT", label: "Tinta de Contraste" },
  { code: "CARRIER_SUSPENSION", label: "Veículo / Suspensão" }
] as const;

export type PmConsumableType = (typeof PM_CONSUMABLE_TYPES)[number]["code"];

export const PM_CONSUMABLE_TYPE_CODES: readonly string[] = PM_CONSUMABLE_TYPES.map(t => t.code);

export function formatPmConsumableType(code: string | null | undefined): string {
  return PM_CONSUMABLE_TYPES.find(t => t.code === code)?.label ?? String(code ?? "");
}

/** Técnica / aplicação, quando aplicável. Nenhuma regra técnica é derivada daqui — é classificação. */
export const PM_TECHNIQUES = [
  { code: "", label: "Não informada" },
  { code: "DRY", label: "Via Seca" },
  { code: "WET", label: "Via Úmida" },
  { code: "VISIBLE", label: "Visível" },
  { code: "FLUORESCENT", label: "Fluorescente" },
  { code: "OTHER_NA", label: "Outra / Não Aplicável" }
] as const;

export type PmTechnique = (typeof PM_TECHNIQUES)[number]["code"];

export function formatPmTechnique(code: string | null | undefined): string {
  return PM_TECHNIQUES.find(t => t.code === (code ?? ""))?.label ?? String(code ?? "");
}

export type PmConsumableStatus = "ACTIVE" | "INACTIVE";

export interface PmConsumable {
  id: string;
  type: PmConsumableType;
  brand: string;
  product: string;
  lot: string;
  /** "aaaa-mm-dd", opcional. */
  manufacturingDate: string;
  /** "aaaa-mm-dd", obrigatória. */
  validity: string;
  technique: PmTechnique;
  status: PmConsumableStatus;
  observations: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface PmConsumableFilters {
  type?: string;
  /** Busca por marca, produto ou lote. */
  search?: string;
}

/** Mensagens por campo — nunca um "dados inválidos" genérico. */
export function validatePmConsumable(input: Partial<PmConsumable>): string[] {
  const errors: string[] = [];
  if (!input.type || !PM_CONSUMABLE_TYPE_CODES.includes(input.type)) {
    errors.push("Informe o Tipo do consumível (Partículas Magnéticas, Tinta de Contraste ou Veículo / Suspensão).");
  }
  if (!input.brand || !input.brand.trim()) errors.push("Informe a Marca.");
  if (!input.product || !input.product.trim()) errors.push("Informe o Produto / Nome Comercial.");
  if (!input.lot || !input.lot.trim()) errors.push("Informe o Lote.");
  if (!input.validity || !input.validity.trim()) {
    errors.push("Informe a Data de Validade.");
  } else if (!/^\d{4}-\d{2}-\d{2}$/.test(input.validity)) {
    errors.push("A Data de Validade deve ser uma data (aaaa-mm-dd).");
  }
  if (input.manufacturingDate && !/^\d{4}-\d{2}-\d{2}$/.test(input.manufacturingDate)) {
    errors.push("A Data de Fabricação deve ser uma data (aaaa-mm-dd).");
  }
  if (input.manufacturingDate && input.validity && /^\d{4}-\d{2}-\d{2}$/.test(input.validity) && input.validity < input.manufacturingDate) {
    errors.push("A Data de Validade não pode ser anterior à Data de Fabricação.");
  }
  if (input.technique && !PM_TECHNIQUES.some(t => t.code === input.technique)) {
    errors.push("Técnica / Aplicação inválida.");
  }
  return errors;
}

/** "Magnaflux — 7HF (Lote 24A03)" */
export function formatPmConsumableLabel(item: PmConsumable): string {
  const lot = item.lot ? ` (Lote ${item.lot})` : "";
  return `${item.brand} — ${item.product}${lot}`;
}

/** Mesma regra civil do LP: sem validade não se afirma vencimento. */
export function isPmConsumableExpired(validity: string | null | undefined, today: string = todayIso()): boolean {
  return isLpConsumableExpired(validity, today);
}

export { formatCivilDateBR };

/** Filtro da aba: tipo e busca por marca, produto ou lote. */
export function applyPmConsumableFilters(items: readonly PmConsumable[], filters: PmConsumableFilters): PmConsumable[] {
  const term = (filters.search ?? "").trim().toLowerCase();
  return items.filter(item => {
    if (filters.type && item.type !== filters.type) return false;
    if (term && ![item.brand, item.product, item.lot].some(v => v.toLowerCase().includes(term))) return false;
    return true;
  });
}

/**
 * O que o Relatório PM pode oferecer: os ATIVOS do tipo, mais o que o
 * relatório já aponta — reabrir um relatório antigo nunca perde nem reescreve
 * o consumível com que foi emitido. Vencido continua no histórico; a tela
 * avisa, não apaga.
 */
export function selectablePmConsumables(
  items: readonly PmConsumable[],
  type: PmConsumableType,
  currentlyLinkedId?: string | null
): PmConsumable[] {
  return items
    .filter(item => item.type === type)
    .filter(item => item.status === "ACTIVE" || (!!currentlyLinkedId && item.id === currentlyLinkedId))
    .sort((a, b) => formatPmConsumableLabel(a).localeCompare(formatPmConsumableLabel(b), "pt-BR", { numeric: true }));
}
